import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * JsonLd — renders a Schema.org JSON-LD `<script type="application/ld+json">`
 * inside the document `<head>` via react-helmet-async so it participates in the
 * same SSR/prerender cascade as our OpenGraph tags. Accepts any JSON-serialisable
 * structured-data object (or an array of them).
 *
 * Usage:
 *   <JsonLd data={{ "@context": "https://schema.org", "@type": "BlogPosting", ... }} />
 */
export default function JsonLd({ data }) {
  if (!data) return null;
  const payload = Array.isArray(data) ? data : [data];
  return (
    <Helmet>
      {payload.map((obj, i) => (
        <script
          key={i}
          type="application/ld+json"
          // Helmet passes children of <script> through as raw text — exactly what
          // Google expects. Stringifying here (not via dangerouslySetInnerHTML)
          // keeps the API idiomatic and DOM-safe.
        >
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

/** Build a `School` schema object. Only includes AggregateRating when there's
 *  at least one review, and only lists up to `reviewLimit` reviews so the
 *  payload stays crawler-friendly. */
export function schoolSchema(s, canonicalUrl, reviews = [], reviewLimit = 5) {
  if (!s) return null;
  const image = s.image_url || (Array.isArray(s.gallery_images) && s.gallery_images[0]) || undefined;
  const address = s.city || s.district || undefined;
  const obj = {
    "@context": "https://schema.org",
    "@type": "School",
    name: s.name,
    url: canonicalUrl,
    ...(image ? { image } : {}),
    ...(s.description ? { description: (s.description || "").replace(/\s+/g, " ").trim().slice(0, 500) } : {}),
    ...(address ? { address: { "@type": "PostalAddress", addressLocality: address, addressCountry: "ID" } } : {}),
  };
  const rating = Number(s.avg_rating || 0);
  const count = Number(s.review_count || 0);
  if (rating > 0 && count > 0) {
    obj.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rating.toFixed(2),
      reviewCount: count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  const trimmed = (reviews || []).slice(0, reviewLimit);
  if (trimmed.length > 0) {
    obj.review = trimmed.map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: Number(r.rating || r.avg || 0),
        bestRating: 5,
        worstRating: 1,
      },
      author: { "@type": "Person", name: r.user_name || r.reviewer_name || "Anonim" },
      ...(r.created_at ? { datePublished: r.created_at } : {}),
      ...(r.body ? { reviewBody: (r.body || "").replace(/\s+/g, " ").trim().slice(0, 500) } : {}),
    }));
  }
  return obj;
}

/** Build a `BlogPosting` schema object. */
export function blogPostingSchema(p, canonicalUrl) {
  if (!p) return null;
  const authorName = p.author_name || "Redaksi The School Reports";
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: (p.title || "").slice(0, 110),
    ...(p.excerpt ? { description: p.excerpt } : {}),
    ...(p.cover_url ? { image: p.cover_url } : {}),
    ...(p.created_at ? { datePublished: p.created_at } : {}),
    ...(p.updated_at ? { dateModified: p.updated_at } : {}),
    author: {
      "@type": "Person",
      name: authorName,
      ...(p.author_title ? { jobTitle: p.author_title } : {}),
    },
    publisher: {
      "@type": "Organization",
      name: "The School Reports",
      logo: {
        "@type": "ImageObject",
        url: "https://theschoolreports.id/logo512.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
  };
}

/** Deterministic runtime owner for the article's BlogPosting JSON-LD.
 *
 * Upserts the SINGLE element `#article-blogposting-jsonld` — the same id the
 * prerender snapshot (journalSeoRenderer.js) emits. If the snapshot element
 * exists, its textContent is REPLACED in place (edited articles surface fresh
 * values); if no snapshot exists (article published after the last build), the
 * element is created. Either way the final rendered HTML contains exactly one
 * BlogPosting, with current data. The element intentionally has NO data-rh
 * attribute so react-helmet-async's tag sweep never removes or duplicates it.
 * Callers must remove() the returned element on unmount (handled by the
 * useEffect cleanup in BlogDetail). */
export function upsertArticleJsonLd(data) {
  if (typeof document === "undefined" || !data) return null;
  const ID = "article-blogposting-jsonld";
  // Enforce uniqueness first — remove any stray duplicates of the id.
  const all = document.querySelectorAll(`script#${ID}`);
  all.forEach((el, i) => { if (i > 0 && el.parentNode) el.parentNode.removeChild(el); });
  let el = document.getElementById(ID);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
}

/** Build a `DiscussionForumPosting` schema object. */
export function discussionForumPostingSchema(thread, replies = [], canonicalUrl) {
  if (!thread) return null;
  const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return {
    "@context": "https://schema.org",
    "@type": "DiscussionForumPosting",
    headline: (thread.title || "").slice(0, 110),
    articleBody: stripHtml(thread.body).slice(0, 800),
    ...(thread.created_at ? { datePublished: thread.created_at } : {}),
    ...(thread.last_activity || thread.updated_at
      ? { dateModified: thread.last_activity || thread.updated_at }
      : {}),
    author: {
      "@type": "Person",
      name: thread.user_name || "Anonim",
    },
    ...(canonicalUrl ? { url: canonicalUrl } : {}),
    interactionStatistic: [
      {
        "@type": "InteractionCounter",
        interactionType: "https://schema.org/CommentAction",
        userInteractionCount: Number(thread.reply_count || replies.length || 0),
      },
      {
        "@type": "InteractionCounter",
        interactionType: "https://schema.org/LikeAction",
        userInteractionCount: Number(thread.upvotes || 0),
      },
    ],
    comment: (replies || []).slice(0, 20).map((r) => ({
      "@type": "Comment",
      text: stripHtml(r.body).slice(0, 500),
      author: { "@type": "Person", name: r.user_name || "Anonim" },
      ...(r.created_at ? { dateCreated: r.created_at } : {}),
    })),
  };
}
